import { useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, useTheme } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { ReferenceInput, ReferenceArrayInput, SelectInput, CheckboxGroupInput, required, useChoicesContext, useTranslate, useRecordContext } from 'react-admin';
import { styled } from '@mui/material/styles';
import { Palette } from '@/themes/themeOverrides/Palette';

const StyledCheckboxGroup = styled('div')(({ theme, requiredIds }) => ({
    '& .MuiFormGroup-root': {
        '& .MuiFormControlLabel-root': {
            margin: 0,
            padding: '10px 12px',
            marginBottom: '0px',
            borderRadius: '8px',
            '& .MuiCheckbox-root': {
                padding: '0',
                margin: '0 8px 0 0',
                height: 'auto',
            },
            '& .MuiTypography-root': {
                padding: '0px',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                fontSize: '14px',
            },
            '&:hover': {
                background: Palette.yellow.yellow50,
                [theme.breakpoints.down('md')]: {
                    background: 'transparent',
                },
            },
        },
        ...(requiredIds &&
            requiredIds.length > 0 && {
                ...requiredIds.reduce((acc, id) => {
                    acc[`& label[for$="serviceCompanyIds_${id}"]`] = {
                        pointerEvents: 'none',
                        '& svg': {
                            fill: Palette.white,
                            '& path': {
                                stroke: Palette.gray.gray500,
                            },
                        },
                    };
                    return acc;
                }, {}),
            }),
    },
}));

const ServiceCompaniesCheckboxGroupInner = ({ requireSelection = true, lockedCompanyName = '', requireDefault = true }) => {
    const { allChoices } = useChoicesContext() || { allChoices: [] };
    const { setValue, getValues } = useFormContext();
    const selectedCompaniesWatch = useWatch({ name: 'serviceCompanyIds' });
    const translate = useTranslate();
    const record = useRecordContext();

    const selectedCompanies = useMemo(() => selectedCompaniesWatch ?? [], [selectedCompaniesWatch]);
    const isNewRecord = !record?.id;
    const userHasIntervened = useRef(false);

    const defaultCompanyIds = useMemo(() => {
        if (!allChoices || allChoices.length === 0) return [];
        return allChoices.filter(choice => choice.isDefault === true || (lockedCompanyName && choice.name === lockedCompanyName)).map(choice => choice.id);
    }, [allChoices, lockedCompanyName]);

    const requiredCompanyIds = useMemo(() => {
        if (!allChoices || allChoices.length === 0) return [];
        return allChoices.filter(choice => choice.isRequired === true || (lockedCompanyName && choice.name === lockedCompanyName)).map(choice => choice.id);
    }, [allChoices, lockedCompanyName]);

    useEffect(() => {
        const requiredOnly = Array.from(new Set(requiredCompanyIds ?? []));
        const shouldAutoCheck = !Array.isArray(selectedCompanies) || (isNewRecord && selectedCompanies.length === 0 && !userHasIntervened.current);

        if (shouldAutoCheck) {
            const autoCheckedIds = Array.from(new Set([...(defaultCompanyIds ?? []), ...requiredOnly]));
            if (requireDefault && autoCheckedIds.length > 0) {
                const currentDefaultId = getValues('defaultServiceCompanyId');
                if (!currentDefaultId) {
                    const autoDefaultId =
                        defaultCompanyIds?.find(id => autoCheckedIds.includes(id)) ?? requiredOnly.find(id => autoCheckedIds.includes(id)) ?? autoCheckedIds[0];
                    if (autoDefaultId) {
                        setValue('defaultServiceCompanyId', autoDefaultId, { shouldDirty: false });
                    }
                }
            }
            if (autoCheckedIds.length > 0) {
                setValue('serviceCompanyIds', autoCheckedIds, { shouldDirty: false });
            }
            return;
        }
        const missingRequired = requiredOnly.filter(id => !selectedCompanies.includes(id));
        if (missingRequired.length > 0) {
            setValue('serviceCompanyIds', [...selectedCompanies, ...missingRequired], { shouldDirty: true });
        }
    }, [selectedCompanies, defaultCompanyIds, requiredCompanyIds, setValue, getValues, isNewRecord, requireDefault]);

    useEffect(() => {
        if (!requireDefault || !Array.isArray(selectedCompanies) || selectedCompanies.length === 0) {
            return;
        }

        const currentDefaultId = getValues('defaultServiceCompanyId');
        if (currentDefaultId && selectedCompanies.includes(currentDefaultId)) {
            return;
        }

        const preferredDefaultId =
            selectedCompanies.find(id => defaultCompanyIds.includes(id)) ??
            selectedCompanies.find(id => requiredCompanyIds.includes(id)) ??
            selectedCompanies[0];

        if (preferredDefaultId && currentDefaultId !== preferredDefaultId) {
            setValue('defaultServiceCompanyId', preferredDefaultId, { shouldDirty: false });
        }
    }, [selectedCompanies, defaultCompanyIds, requiredCompanyIds, getValues, setValue, requireDefault]);

    useEffect(() => {
        if (!requireDefault || !defaultCompanyIds || defaultCompanyIds.length === 0) {
            return;
        }

        const currentDefaultId = getValues('defaultServiceCompanyId');
        if (currentDefaultId) {
            return;
        }

        const fallbackDefaultId = (selectedCompanies ?? []).find(id => defaultCompanyIds.includes(id));

        if (fallbackDefaultId) {
            setValue('defaultServiceCompanyId', fallbackDefaultId, { shouldDirty: false });
        }
    }, [selectedCompanies, defaultCompanyIds, getValues, setValue, requireDefault]);

    const handleChange = useCallback(
        selection => {
            userHasIntervened.current = true;

            const filteredSelection = Array.from(new Set([...selection, ...requiredCompanyIds]));
            const selectedDefault = getValues('defaultServiceCompanyId');
            if (filteredSelection.length === 1) {
                setValue('defaultServiceCompanyId', filteredSelection[0]);
            } else if (selectedDefault && !filteredSelection.includes(selectedDefault)) {
                setValue('defaultServiceCompanyId', null);
            }

            setValue('serviceCompanyIds', filteredSelection, { shouldDirty: true });
        },
        [requiredCompanyIds, getValues, setValue],
    );

    const validateSelection = useCallback(
        value => {
            const selected = Array.isArray(value) ? value : [];
            const diff = requiredCompanyIds.filter(item => !selected.includes(item));

            if (diff.length > 0) {
                const names = diff.map(item => {
                    const choice = allChoices?.find(c => c.id === item);
                    return choice?.name ?? item;
                });
                return translate('common.validation.mustContainNames', { names: names.join(', ') });
            }

            return undefined;
        },
        [requiredCompanyIds, allChoices, translate],
    );

    return (
        <StyledCheckboxGroup requiredIds={requiredCompanyIds}>
            <CheckboxGroupInput
                validate={requireSelection ? [required(translate('common.validation.required')), validateSelection] : [validateSelection]}
                fullWidth
                onChange={handleChange}
                label={translate('menu.serviceCompanies')}
                row={false}
                sx={
                    selectedCompanies?.length > 0
                        ? {
                              '& .MuiFormLabel-root.MuiInputLabel-root': {
                                  transform: 'translate(14px, -11px) scale(1)',
                              },
                          }
                        : {}
                }
            />
        </StyledCheckboxGroup>
    );
};

ServiceCompaniesCheckboxGroupInner.propTypes = {
    requireSelection: PropTypes.bool,
    lockedCompanyName: PropTypes.string,
    requireDefault: PropTypes.bool,
};

const ServiceCompaniesCheckboxGroup = ({ requireSelection = true, lockedCompanyName = '', requireDefault = true }) => {
    return (
        <ReferenceArrayInput source='serviceCompanyIds' reference='service-company'>
            <ServiceCompaniesCheckboxGroupInner requireSelection={requireSelection} lockedCompanyName={lockedCompanyName} requireDefault={requireDefault} />
        </ReferenceArrayInput>
    );
};

ServiceCompaniesCheckboxGroup.propTypes = {
    requireSelection: PropTypes.bool,
    lockedCompanyName: PropTypes.string,
    requireDefault: PropTypes.bool,
};

const DefaultServiceCompanyInput = ({ requireDefault = true }) => {
    const translate = useTranslate();
    const selectedCompanies = useWatch({ name: 'serviceCompanyIds' }) ?? [];

    return (
        selectedCompanies.length > 0 && (
            <Box className='default-service-company-input'>
                <ReferenceInput source='defaultServiceCompanyId' reference='service-company' filter={{ id_in: selectedCompanies }}>
                    <SelectInput
                        label={translate('resources.contractor.labels.defaultServiceCompanyId')}
                        validate={requireDefault ? required(translate('common.validation.required')) : undefined}
                        emptyText={translate(requireDefault ? 'common.all' : 'common.empty')}
                        fullWidth
                    />
                </ReferenceInput>
            </Box>
        )
    );
};

DefaultServiceCompanyInput.propTypes = {
    requireDefault: PropTypes.bool,
};

export const ContractorEditServiceCompanies = ({ requireSelection = true, requireDefault = true, lockedCompanyName = '' }) => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '32px',
                width: '100%',
                margin: '36px 0 44px',
                [theme.breakpoints.down('md')]: { gridTemplateColumns: '1fr', gap: '24px', margin: '36px 0 24px' },
            }}
        >
            <ServiceCompaniesCheckboxGroup requireSelection={requireSelection} lockedCompanyName={lockedCompanyName} requireDefault={requireDefault} />
            <DefaultServiceCompanyInput requireDefault={requireDefault} />
        </Box>
    );
};

ContractorEditServiceCompanies.propTypes = {
    requireSelection: PropTypes.bool,
    requireDefault: PropTypes.bool,
    lockedCompanyName: PropTypes.string,
};
